import { useEffect, useMemo, useState } from "react";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import {
  ArrowDown,
  ArrowUp,
  BookOpen,
  ChevronDown,
  FileText,
  MoreVertical,
  Plus,
  RotateCcw,
} from "lucide-react";
import { Button, Field, Modal, SearchIcon, SelectField, StatusBadge, TextareaField, type Column } from "@fawkes/ui";

import { IndeelbareTabel } from "../components/IndeelbareTabel";
import { formatDateTime, formatEuro } from "../i18n/format";

/**
 * Financieel → Kasboek (maintainer design 2026-08-17): the cash book — income and expenses booked by hand at
 * the counter, with the drawer's running balance beside every row. Its own screen, not a tab of the invoice
 * overview: this is where money is BOOKED, not where invoices are read.
 *
 * <p>The Saldo column is the drawer's running balance, computed server-side from everything booked before the
 * page (see the api's {@code KasboekService}) — so it stays right when a booking is entered with an earlier
 * date. It shows only while one drawer is in view; a running balance over two drawers means nothing.
 *
 * <p>Een boeking is altijd definitief. Er stond een schakelaar "Direct verwerken" die hem open kon
 * laten, met een kengetal en een "Open"-markering erbij; die keuze bestaat niet meer, dus die drie
 * zijn weg. Het veld blijft op de lijn staan voor wat er al in de boeken staat.
 */

type Soort = "INKOMST" | "UITGAVE";

interface Boeking {
  id: number;
  soort: Soort;
  geboektOp: string;
  vestigingId: number | null;
  vestigingNaam: string | null;
  kas: string;
  grootboekRekening: string | null;
  grootboekOmschrijving: string | null;
  omschrijving: string;
  bedrag: number;
  saldo: number | null;
  relatieId: number | null;
  tegenrekening: string | null;
  referentie: string | null;
  notitie: string | null;
  verwerkt: boolean;
  aangemaaktDoor: string | null;
}

interface KasboekPage {
  items: Boeking[];
  total: number;
  page: number;
  size: number;
  kop: {
    kasSaldo: number;
    laatsteMutatie: string | null;
    inkomstenVandaag: number;
    inkomstenVandaagAantal: number;
    uitgavenVandaag: number;
    uitgavenVandaagAantal: number;
    verwerkteBoekingen: number;
  } | null;
}

/**
 * The shelter runs ONE cash drawer, so there is nothing to choose: no Kas filter, no Kas field in the
 * dialog and no Kas column. The api keeps the column (a second drawer would be a data question, not a
 * screen rewrite), and every booking this screen writes carries this name.
 */
const DE_KAS = "Hoofdkas";

const PAGINA_KEUZES = [20, 50, 100] as const;

export function Kasboek() {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const [van, setVan] = useState(() => eersteVanDeMaand());
  const [tot, setTot] = useState(() => laatsteVanDeMaand());
  const [soort, setSoort] = useState("");
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [sort, setSort] = useState("nieuwste");
  const [page, setPage] = useState(1);
  const [perPagina, setPerPagina] = useState<number>(20);
  const [menuVoor, setMenuVoor] = useState<number | null>(null);
  const [dialoog, setDialoog] = useState<{ boeking: Boeking | null } | null>(null);

  useEffect(() => {
    const handle = setTimeout(() => { setDebounced(query.trim()); setPage(1); }, 300);
    return () => clearTimeout(handle);
  }, [query]);
  useEffect(() => { setPage(1); }, [van, tot, soort, perPagina]);

  useEffect(() => {
    if (menuVoor == null) return;
    const buiten = (e: MouseEvent) => {
      if ((e.target as HTMLElement | null)?.closest(".fawkes-fin__rowmenu")) return;
      setMenuVoor(null);
    };
    const toets = (e: KeyboardEvent) => { if (e.key === "Escape") setMenuVoor(null); };
    document.addEventListener("mousedown", buiten);
    document.addEventListener("keydown", toets);
    return () => {
      document.removeEventListener("mousedown", buiten);
      document.removeEventListener("keydown", toets);
    };
  }, [menuVoor]);

  const url = useMemo(() => {
    const params = new URLSearchParams();
    if (van) params.set("van", van);
    if (tot) params.set("tot", tot);
    if (soort) params.set("soort", soort);
    if (debounced) params.set("q", debounced);
    params.set("sort", sort);
    params.set("page", String(page));
    params.set("size", String(perPagina));
    return `kasboek?${params.toString()}`;
  }, [van, tot, soort, debounced, sort, page, perPagina]);

  const { data, isLoading, isError, refetch } = useCustom<KasboekPage>({ url, method: "get" });
  const rijen = data?.data?.items ?? [];
  const totaal = data?.data?.total ?? 0;
  const kop = data?.data?.kop ?? null;

  const paginas = Math.max(1, Math.ceil(totaal / perPagina));

  // De sortering blijft serverwerk (de url hierboven draagt hem); de tabel meldt alleen de bedoeling.
  const sorteerStand = sort === "bedrag"
    ? { key: "bedrag", dir: "desc" as const }
    : { key: "datum", dir: (sort === "oudste" ? "asc" : "desc") as "asc" | "desc" };

  const kolommen: Column<Boeking>[] = [
    {
      key: "datum", header: t("kasboek.kolom.datum"), sortKey: "datum",
      cell: (b) => <span className="tabular">{formatDateTime(b.geboektOp, locale)}</span>,
    },
    {
      key: "type", header: t("kasboek.kolom.type"),
      cell: (b) => (
        <StatusBadge tone={b.soort === "INKOMST" ? "success" : "danger"}>
          {t(`kasboek.soort.${b.soort}`)}
        </StatusBadge>
      ),
    },
    {
      key: "omschrijving", header: t("kasboek.kolom.omschrijving"),
      cell: (b) => (
        <button type="button" className="fawkes-fin__link" onClick={() => setDialoog({ boeking: b })}>
          {b.omschrijving}
        </button>
      ),
    },
    {
      key: "grootboek", header: t("omzet.kolom.grootboek"),
      cell: (b) => (b.grootboekRekening
        ? [b.grootboekRekening, b.grootboekOmschrijving].filter(Boolean).join(" - ")
        : "—"),
    },
    {
      key: "bedrag", header: t("kasboek.kolom.bedrag"), sortKey: "bedrag",
      cell: (b) => (
        <span className={b.soort === "UITGAVE" ? "is-num tabular is-danger" : "is-num tabular"}>
          {b.soort === "UITGAVE" ? `-${formatEuro(b.bedrag, locale)}` : formatEuro(b.bedrag, locale)}
        </span>
      ),
    },
    {
      key: "saldo", header: t("kasboek.kolom.saldo"),
      cell: (b) => (
        <span className="is-num tabular">{b.saldo == null ? "—" : formatEuro(b.saldo, locale)}</span>
      ),
    },
    {
      key: "acties", header: "", vast: true,
      cell: (b) => (
        <div className="fawkes-fin__rowmenu">
          <button
            type="button" className="fawkes-fin__kebab" title={t("common.more")}
            aria-haspopup="true" aria-expanded={menuVoor === b.id}
            onClick={(e) => { e.stopPropagation(); setMenuVoor(menuVoor === b.id ? null : b.id); }}
          >
            <MoreVertical size={16} />
          </button>
          {menuVoor === b.id && (
            <div className="fawkes-fin__rowmenu-items" role="menu">
              <button type="button" role="menuitem"
                onClick={() => { setMenuVoor(null); setDialoog({ boeking: b }); }}>
                {t("kasboek.actie.wijzigen")}
              </button>
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="fawkes-fin fawkes-kas">
      <header className="fawkes-fin__head fawkes-kas__head">
        <span className="fawkes-fin__head-icon" aria-hidden="true"><BookOpen size={26} /></span>
        <div className="fawkes-fin__head-text">
          <h2>{t("kasboek.title")}</h2>
          <p>{t("kasboek.subtitle")}</p>
          <Button onClick={() => setDialoog({ boeking: null })}>
            <Plus size={16} /> {t("kasboek.nieuw")}
          </Button>
        </div>
        {/* Geen kassaldo-tegel: het saldo staat ook in de kolom Saldo van de laatste boeking, en een
            los getal bovenin dat daar los van loopt leest als een tweede waarheid (Maarten, 2026-10-23). */}
        <div className="fawkes-fin__kpis">
          <Kpi
            icon={<ArrowUp size={18} />} value={formatEuro(kop?.inkomstenVandaag ?? 0, locale)}
            label={t("kasboek.kpi.inkomstenVandaag")}
            hint={t("kasboek.kpi.boekingen", { args: [String(kop?.inkomstenVandaagAantal ?? 0)] })}
          />
          <Kpi
            icon={<ArrowDown size={18} />} value={formatEuro(kop?.uitgavenVandaag ?? 0, locale)}
            label={t("kasboek.kpi.uitgavenVandaag")} danger
            hint={t("kasboek.kpi.boekingen", { args: [String(kop?.uitgavenVandaagAantal ?? 0)] })}
          />
          <Kpi
            icon={<FileText size={18} />} value={String(totaal)}
            label={t("kasboek.kpi.boekingenLabel")} accent
          />
        </div>
      </header>

      <section className="fawkes-kas__card">
        <h3>{t("kasboek.recente")}</h3>
        <div className="fawkes-kas__filters">
          <Field
            label={t("kasboek.filter.van")} type="date" value={van}
            onChange={(e) => setVan(e.target.value)}
          />
          <Field
            label={t("kasboek.filter.tot")} type="date" value={tot}
            onChange={(e) => setTot(e.target.value)}
          />
          <SelectField
            label={t("kasboek.filter.type")} value={soort} onChange={(e) => setSoort(e.target.value)}
            options={[
              { value: "", label: t("financieel.filter.alle") },
              { value: "INKOMST", label: t("kasboek.soort.INKOMST") },
              { value: "UITGAVE", label: t("kasboek.soort.UITGAVE") },
            ]}
          />
          <div className="fawkes-fin__searchcell">
            <span className="fui-field__label" aria-hidden="true">&nbsp;</span>
            <label className="fawkes-fin__search">
              <span className="fawkes-fin__search-icon" aria-hidden="true">
                <SearchIcon width={18} height={18} />
              </span>
              <input
                value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder={t("kasboek.filter.zoek")} aria-label={t("kasboek.filter.zoek")}
              />
            </label>
          </div>
          <div className="fawkes-fin__filters-actions">
            <span className="fui-field__label" aria-hidden="true">&nbsp;</span>
            <Button
              variant="secondary"
              onClick={() => {
                setVan(eersteVanDeMaand()); setTot(laatsteVanDeMaand());
                setSoort(""); setQuery(""); setSort("nieuwste");
              }}
            >
              <RotateCcw size={14} /> {t("financieel.filter.wissen")}
            </Button>
          </div>
        </div>

        {isLoading && <p className="fawkes-fin__state">{t("common.loading")}</p>}
        {isError && (
          <p className="fawkes-fin__state fawkes-fin__state--error">
            {t("common.loadError")}{" "}
            <Button variant="secondary" onClick={() => refetch()}>{t("common.retry")}</Button>
          </p>
        )}
        {!isLoading && !isError && rijen.length === 0 && (
          <p className="fawkes-fin__state">{t("kasboek.leeg")}</p>
        )}

        {!isLoading && !isError && rijen.length > 0 && (
          <div className="fawkes-fin__tablewrap">
            <IndeelbareTabel<Boeking>
              tabelKey="kasboek"
              columns={kolommen}
              rows={rijen}
              getRowKey={(b) => String(b.id)}
              empty={t("kasboek.leeg")}
              sort={sorteerStand}
              onSortChange={(next) => setSort(next.key === "bedrag" ? "bedrag" : next.dir === "asc" ? "oudste" : "nieuwste")}
            />
          </div>
        )}

        <div className="fawkes-kas__pager">
          <span className="fawkes-fin__count">
            {t("kasboek.resultaten", {
              args: [String(totaal === 0 ? 0 : (page - 1) * perPagina + 1),
                String(Math.min(page * perPagina, totaal)), String(totaal)],
            })}
          </span>
          <div className="fawkes-kas__pager-right">
            <Button variant="secondary" disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}>
              {t("common.previous")}
            </Button>
            <span className="fawkes-fin__pageinfo">{page}</span>
            <Button variant="secondary" disabled={page >= paginas} onClick={() => setPage((p) => p + 1)}>
              {t("common.next")}
            </Button>
            <select
              value={String(perPagina)} aria-label={t("kasboek.perPagina")}
              onChange={(e) => setPerPagina(Number(e.target.value))}
            >
              {PAGINA_KEUZES.map((n) => (
                <option key={n} value={n}>{t("kasboek.perPaginaN", { args: [String(n)] })}</option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {dialoog && (
        <BoekingDialoog
          boeking={dialoog.boeking}
          onClose={() => setDialoog(null)}
          onSaved={() => { setDialoog(null); refetch(); }}
        />
      )}
    </div>
  );
}

/** Nieuwe kasboeking / boeking wijzigen — the dialog of the design, same fields in the same order. */
function BoekingDialoog({ boeking, onClose, onSaved }: {
  boeking: Boeking | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useTranslate();
  const [soort, setSoort] = useState<Soort>(boeking?.soort ?? "INKOMST");
  const [datum, setDatum] = useState(() => (boeking?.geboektOp ?? new Date().toISOString()).slice(0, 10));
  const [grootboek, setGrootboek] = useState(boeking?.grootboekRekening ?? "");
  const [omschrijving, setOmschrijving] = useState(boeking?.omschrijving ?? "");
  const [tegenrekening, setTegenrekening] = useState(boeking?.tegenrekening ?? "");
  const [bedrag, setBedrag] = useState(boeking ? String(boeking.bedrag) : "");
  const [referentie, setReferentie] = useState(boeking?.referentie ?? "");
  const [notitie, setNotitie] = useState(boeking?.notitie ?? "");
  const [fout, setFout] = useState<string | null>(null);

  // The ledger accounts to book on, from the accounts screen's own feed.
  const { data: rekeningFeed } = useCustom<{ rekeningNummer: string; omschrijving: string | null }[]>({
    url: "grootboek-rekeningen", method: "get",
  });
  const rekeningen = useMemo(() => {
    const rijen = rekeningFeed?.data;
    return Array.isArray(rijen) ? rijen : [];
  }, [rekeningFeed]);

  // The branch the booking belongs to: the one the shell is switched to.
  const { data: vestigingFeed } = useCustom<{ id: number; naam: string }>({
    url: "vestigingen/current", method: "get",
  });
  const vestiging = vestigingFeed?.data ?? null;

  const { mutate, isLoading: bezig } = useCustomMutation();

  const opslaan = () => {
    const bedragNum = Number(bedrag.replace(",", ".").trim());
    if (!omschrijving.trim()) { setFout(t("kasboek.fout.omschrijving")); return; }
    if (!Number.isFinite(bedragNum) || bedragNum <= 0) { setFout(t("kasboek.fout.bedrag")); return; }
    if (!vestiging?.id) { setFout(t("kasboek.fout.vestiging")); return; }
    setFout(null);

    const gekozen = rekeningen.find((r) => r.rekeningNummer === grootboek);
    const payload = {
      soort,
      // The dialog books a day; the time is when it was entered, which is what the list orders on.
      geboektOp: `${datum}T${lokaleTijd()}`,
      vestigingId: vestiging.id,
      kas: boeking?.kas ?? DE_KAS,
      grootboekRekening: grootboek || null,
      grootboekOmschrijving: gekozen?.omschrijving ?? null,
      omschrijving: omschrijving.trim(),
      bedrag: bedragNum,
      relatieId: null,
      tegenrekening: tegenrekening.trim() || null,
      referentie: referentie.trim() || null,
      notitie: notitie.trim() || null,
      // Een boeking is definitief. Er was een schakelaar "Direct verwerken" die hem open kon laten;
      // die keuze bestaat niet meer, dus hij gaat altijd verwerkt de deur uit.
      verwerkt: true,
    };

    mutate(
      {
        url: boeking ? `kasboek/${boeking.id}` : "kasboek",
        method: boeking ? "put" : "post",
        values: payload,
      },
      { onSuccess: () => onSaved(), onError: () => setFout(t("kasboek.fout.opslaan")) },
    );
  };

  return (
    <Modal
      open
      size="wide"
      title={t(boeking ? "kasboek.dialoog.wijzig" : "kasboek.dialoog.nieuw")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={opslaan} disabled={bezig}>{t("kasboek.dialoog.opslaan")}</Button>
        </>
      }
    >
      <div className="fawkes-kas__form">
        <div className="fawkes-kas__form-grid">
          <div className="fui-field">
            <span className="fui-field__label">{t("kasboek.kolom.type")}</span>
            {/* Two buttons rather than a select: it is a choice of two, and the colour says which. */}
            <div className="fawkes-kas__soort" role="group" aria-label={t("kasboek.kolom.type")}>
              {(["INKOMST", "UITGAVE"] as Soort[]).map((s) => (
                <button
                  key={s} type="button" aria-pressed={soort === s}
                  className={`fawkes-kas__soort-btn${soort === s ? " is-active" : ""}`}
                  onClick={() => setSoort(s)}
                >
                  {t(`kasboek.soort.${s}`)}
                </button>
              ))}
            </div>
          </div>
          <Field
            label={t("kasboek.veld.datum")} type="date" value={datum}
            onChange={(e) => setDatum(e.target.value)}
          />
          <Field
            label={t("financieel.filter.vestiging")} value={vestiging?.naam ?? "—"} readOnly
            onChange={() => { /* de vestiging komt uit de schakelaar in de shell */ }}
          />
          <RekeningKiezer
            label={t("omzet.kolom.grootboek")}
            waarde={grootboek}
            rekeningen={rekeningen}
            onKies={setGrootboek}
            leegLabel={t("kasboek.veld.geenRekening")}
            zoekLabel={t("kasboek.veld.rekeningZoek")}
            geenLabel={t("kasboek.veld.rekeningGeen")}
          />
          <Field
            label={t("kasboek.kolom.omschrijving")} value={omschrijving}
            onChange={(e) => setOmschrijving(e.target.value)}
          />
          <Field
            label={t("kasboek.veld.tegenrekening")} value={tegenrekening}
            onChange={(e) => setTegenrekening(e.target.value)}
          />
          <Field
            label={t("kasboek.kolom.bedrag")} value={bedrag} inputMode="decimal" placeholder="0,00"
            onChange={(e) => setBedrag(e.target.value)}
          />
          <Field
            label={t("kasboek.veld.referentie")} value={referentie}
            onChange={(e) => setReferentie(e.target.value)}
          />
          <TextareaField
            label={t("kasboek.veld.notitie")} value={notitie} rows={3}
            onChange={(e) => setNotitie(e.target.value)}
          />
        </div>

        {fout && <p className="fawkes-inboeken__field-error" role="alert">{fout}</p>}
      </div>
    </Modal>
  );
}

/**
 * The ledger-account picker: you type part of the number or the description and pick from what matches.
 * A plain select over eighty accounts means scrolling a list you cannot search — the shelter books on a
 * handful of them and knows their names, so typing is the short path.
 */
function RekeningKiezer({ label, waarde, rekeningen, onKies, leegLabel, zoekLabel, geenLabel }: {
  label: string;
  waarde: string;
  rekeningen: { rekeningNummer: string; omschrijving: string | null }[];
  onKies: (nummer: string) => void;
  leegLabel: string;
  zoekLabel: string;
  geenLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const [zoek, setZoek] = useState("");

  useEffect(() => {
    if (!open) return;
    const buiten = (e: MouseEvent) => {
      if ((e.target as HTMLElement | null)?.closest(".fawkes-kas__kiezer")) return;
      setOpen(false);
    };
    const toets = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", buiten);
    document.addEventListener("keydown", toets);
    return () => {
      document.removeEventListener("mousedown", buiten);
      document.removeEventListener("keydown", toets);
    };
  }, [open]);

  const naam = (r: { rekeningNummer: string; omschrijving: string | null }) =>
    r.omschrijving ? `${r.rekeningNummer} - ${r.omschrijving}` : r.rekeningNummer;
  const gekozen = rekeningen.find((r) => r.rekeningNummer === waarde);
  const naald = zoek.trim().toLowerCase();
  const zichtbaar = naald
    ? rekeningen.filter((r) => naam(r).toLowerCase().includes(naald))
    : rekeningen;

  return (
    <div className="fui-field fawkes-kas__kiezer">
      <span className="fui-field__label">{label}</span>
      <button
        type="button" className="fawkes-fin__multi-btn" aria-haspopup="listbox" aria-expanded={open}
        onClick={() => { setOpen((v) => !v); setZoek(""); }}
      >
        <span>{gekozen ? naam(gekozen) : leegLabel}</span>
        <ChevronDown size={14} aria-hidden="true" />
      </button>
      {open && (
        <div className="fawkes-fin__multi-menu" role="listbox" aria-label={label}>
          <label className="fawkes-fin__multi-zoek">
            <SearchIcon width={14} height={14} aria-hidden="true" />
            <input
              value={zoek} autoFocus placeholder={zoekLabel} aria-label={zoekLabel}
              onChange={(e) => setZoek(e.target.value)}
            />
          </label>
          <div className="fawkes-fin__multi-list">
            <button
              type="button" className="fawkes-kas__kiezer-optie"
              onClick={() => { onKies(""); setOpen(false); }}
            >
              {leegLabel}
            </button>
            {zichtbaar.length === 0 && <p className="fawkes-fin__multi-leeg">{geenLabel}</p>}
            {zichtbaar.map((r) => (
              <button
                key={r.rekeningNummer} type="button" role="option"
                aria-selected={r.rekeningNummer === waarde}
                className={`fawkes-kas__kiezer-optie${r.rekeningNummer === waarde ? " is-active" : ""}`}
                onClick={() => { onKies(r.rekeningNummer); setOpen(false); }}
              >
                {naam(r)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ icon, value, label, hint, accent, danger }: {
  icon: React.ReactNode; value: string; label: string; hint?: string; accent?: boolean; danger?: boolean;
}) {
  return (
    <div className={`fawkes-fin__kpi${accent ? " is-accent" : ""}`}>
      <span className={`fawkes-fin__kpi-icon${danger ? " is-danger" : ""}`} aria-hidden="true">{icon}</span>
      <span className="fawkes-fin__kpi-text">
        <small>{label}</small>
        <strong>{value}</strong>
        {hint && <small className="fawkes-kas__kpi-hint">{hint}</small>}
      </span>
    </div>
  );
}

/** The clock as the operator sees it — toISOString() would stamp UTC and show two hours earlier. */
function lokaleTijd(): string {
  const nu = new Date();
  const tweeCijfers = (n: number) => String(n).padStart(2, "0");
  return `${tweeCijfers(nu.getHours())}:${tweeCijfers(nu.getMinutes())}:${tweeCijfers(nu.getSeconds())}`;
}

/** The default period: this month, like the design's "01-05-2025 - 31-05-2025". */
function eersteVanDeMaand(): string {
  const nu = new Date();
  return isoDatum(new Date(nu.getFullYear(), nu.getMonth(), 1));
}

function laatsteVanDeMaand(): string {
  const nu = new Date();
  return isoDatum(new Date(nu.getFullYear(), nu.getMonth() + 1, 0));
}

/** LOCAL calendar day as yyyy-MM-dd — not toISOString(), which would give yesterday before 02:00 CEST. */
function isoDatum(d: Date): string {
  const maand = String(d.getMonth() + 1).padStart(2, "0");
  const dag = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${maand}-${dag}`;
}
